import {
  ClinicalNoteLegacyPersistedNodeAllowedAttrs,
  ClinicalNoteTransientUiNodeAllowedAttrs,
  type TiptapDocumentLike,
  type TiptapNodeLike,
} from "./clinical-note-content";

export interface ProseMirrorAttrSpec {
  default: unknown;
}

interface ProseMirrorSchemaMemberLike {
  spec?: {
    attrs?: Record<string, unknown>;
  };
}

export interface ProseMirrorSchemaLike {
  nodes: Record<string, ProseMirrorSchemaMemberLike>;
  marks: Record<string, ProseMirrorSchemaMemberLike>;
}

function sortAttrList(attrs: readonly string[]): string[] {
  return [...attrs].sort();
}

function getSchemaMemberAttrKeys(
  members: Record<string, ProseMirrorSchemaMemberLike>,
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(members).map(([memberName, member]) => [
      memberName,
      sortAttrList(Object.keys(member.spec?.attrs ?? {})),
    ]),
  );
}

const TAG_NODE_ATTRS = ["code", "id", "label", "reference"] as const;
const ORDERABLE_TAG_NODE_ATTRS = ["code", "id", "label", "reference", "status", "tempId"] as const;
const TABLE_CELL_ATTRS = ["colspan", "colwidth", "rowspan"] as const;
export const ClinicalNoteBlockquoteAttributes = {
  authorName: null,
  authorReference: null,
  sentAt: null,
} as const;
const TIPTAP_TO_SCHEMA_NODE_NAMES: Record<string, string> = {
  listItem: "list_item",
  bulletList: "bullet_list",
  orderedList: "ordered_list",
  horizontalRule: "horizontal_rule",
  hardBreak: "hard_break",
  tableRow: "table_row",
  tableCell: "table_cell",
  tableHeader: "table_header",
};
const SCHEMA_TO_TIPTAP_NODE_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(TIPTAP_TO_SCHEMA_NODE_NAMES).map(([tiptapName, schemaName]) => [
    schemaName,
    tiptapName,
  ]),
);

export function buildProseMirrorAttrSpecs(
  defaults: Record<string, unknown>,
): Record<string, ProseMirrorAttrSpec> {
  return Object.fromEntries(
    Object.entries(defaults).map(([attrName, defaultValue]) => [
      attrName,
      { default: defaultValue },
    ]),
  );
}

export function getClinicalNoteSchemaShape(schema: ProseMirrorSchemaLike) {
  return {
    nodes: getSchemaMemberAttrKeys(schema.nodes),
    marks: getSchemaMemberAttrKeys(schema.marks),
  };
}

function normalizeNodeName(nodeType: string | undefined, nodeNames: Record<string, string>) {
  if (!nodeType) {
    return nodeType;
  }

  return nodeNames[nodeType] ?? nodeType;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeNodeNamesInContent(
  content: unknown[] | undefined,
  nodeNames: Record<string, string>,
): TiptapNodeLike[] | undefined {
  if (!Array.isArray(content)) {
    return undefined;
  }

  return content.flatMap((node) => {
    const normalizedNode = normalizeNodeNamesInNode(node, nodeNames);
    return normalizedNode ? [normalizedNode] : [];
  });
}

function normalizeNodeNamesInNode(
  node: unknown,
  nodeNames: Record<string, string>,
): TiptapNodeLike | undefined {
  if (!isRecord(node)) {
    return undefined;
  }

  const nodeType = typeof node.type === "string" ? node.type : undefined;
  const content = normalizeNodeNamesInContent(
    Array.isArray(node.content) ? node.content : undefined,
    nodeNames,
  );
  const normalizedNode: TiptapNodeLike = {
    ...node,
    type: normalizeNodeName(nodeType, nodeNames),
  };

  if (content) {
    normalizedNode.content = content;
  }

  return normalizedNode;
}

/**
 * Converts editor-facing TipTap node names to the canonical ProseMirror schema
 * names used by shared clinical note persistence.
 */
export function normalizeClinicalNoteNodeNamesForSchema<T extends TiptapDocumentLike>(doc: T): T {
  return (normalizeNodeNamesInNode(doc, TIPTAP_TO_SCHEMA_NODE_NAMES) ?? doc) as T;
}

/**
 * Converts canonical ProseMirror schema node names back to TipTap's editor-facing
 * node names for web editor reads.
 */
export function normalizeClinicalNoteNodeNamesForTiptap<T extends TiptapDocumentLike>(doc: T): T {
  return (normalizeNodeNamesInNode(doc, SCHEMA_TO_TIPTAP_NODE_NAMES) ?? doc) as T;
}

/**
 * Shared parity contract for persisted clinical note schemas.
 *
 * Web and Hocuspocus both materialize their own ProseMirror schemas, but they
 * must expose the same node/mark names and attr keys for note persistence to
 * round-trip safely across runtimes.
 */
export const ClinicalNoteSchemaContract = {
  nodes: {
    "ai-scribe": sortAttrList(ClinicalNoteTransientUiNodeAllowedAttrs["ai-scribe"]),
    "at-mention": sortAttrList(ClinicalNoteLegacyPersistedNodeAllowedAttrs["at-mention"]),
    attachment: ["type", "url"],
    blockquote: sortAttrList(Object.keys(ClinicalNoteBlockquoteAttributes)),
    bullet_list: [],
    date: ["value"],
    doc: [],
    file: ["contentType", "filename", "id", "url"],
    "fullscript-labs": ["isPracticePay", "nodeId", "panelId", "resourceIds", "tests"],
    hard_break: [],
    hashtag: sortAttrList(ClinicalNoteTransientUiNodeAllowedAttrs.hashtag),
    heading: ["level"],
    horizontal_rule: [],
    "icd10-tag": ["code", "label"],
    "imaging-tag": ORDERABLE_TAG_NODE_ATTRS,
    imagingSuggestion: sortAttrList(ClinicalNoteTransientUiNodeAllowedAttrs.imagingSuggestion),
    "lab-suggestion": sortAttrList(ClinicalNoteLegacyPersistedNodeAllowedAttrs["lab-suggestion"]),
    "labs-tag": ORDERABLE_TAG_NODE_ATTRS,
    "letter-suggestion": sortAttrList(ClinicalNoteTransientUiNodeAllowedAttrs["letter-suggestion"]),
    "letter-tag": TAG_NODE_ATTRS,
    list_item: [],
    "note-template-output": ["sourceAiSoapNoteId", "sourceNoteTemplateId"],
    "note-template-section": ["generatedSignature", "sectionKey"],
    ordered_list: ["order"],
    paragraph: [],
    "private-note": [],
    "referral-tag": TAG_NODE_ATTRS,
    resolve: TAG_NODE_ATTRS,
    "rx-suggestion": sortAttrList(ClinicalNoteLegacyPersistedNodeAllowedAttrs["rx-suggestion"]),
    "rx-tag": TAG_NODE_ATTRS,
    section: ["editable", "title", "type"],
    "slash-suggestion": sortAttrList(ClinicalNoteTransientUiNodeAllowedAttrs["slash-suggestion"]),
    "supplements-suggestion": sortAttrList(
      ClinicalNoteLegacyPersistedNodeAllowedAttrs["supplements-suggestion"],
    ),
    "supplements-tag": TAG_NODE_ATTRS,
    table: [],
    table_cell: TABLE_CELL_ATTRS,
    table_header: TABLE_CELL_ATTRS,
    table_row: [],
    tag: TAG_NODE_ATTRS,
    task: ["assignee", "assigneeDisplay", "completed", "dueDate", "id"],
    text: [],
    vitals: [
      "diastolic",
      "heartRate",
      "height",
      "heightFeet",
      "heightInches",
      "heightInputMode",
      "heightUnit",
      "isEditing",
      "measuredAt",
      "nodeId",
      "observationIds",
      "respiratoryRate",
      "saveError",
      "saved",
      "spo2",
      "systolic",
      "temperature",
      "temperatureUnit",
      "vo2Max",
      "weight",
      "weightUnit",
    ],
  },
  marks: {
    "ai-generated": [],
    bold: [],
    code: [],
    italic: [],
    link: ["href"],
    strike: [],
    textStyle: ["color"],
    underline: [],
  },
} as const;
